/**
 * Homepage demand strip grouping (H15 / H16 P1a).
 * Prefer grouped (C) when ≥2 open requests share a subcategory; else chips (A).
 * Trust: coherent prefs → group CTA; diverging prefs → expand-first; real area labels.
 */

import { formatPreferenceSummary } from "./preferenceAttrs";
import type { ItemRequest } from "@/lib/types";

export const DEMAND_STRIP_GROUP_MIN = 2;

/** Default visible groups before “Show more” (P1.5). Prefer `DEMAND_STRIP_VISIBLE_ROWS`. */
export const DEMAND_STRIP_VISIBLE_GROUPS = 3;

/** @deprecated Prefer combined row cap `DEMAND_STRIP_VISIBLE_ROWS`. */
export const DEMAND_STRIP_VISIBLE_SINGLES = 3;

/** Initial collapsed shelf rows (groups + singles), then Show more. */
export const DEMAND_STRIP_VISIBLE_ROWS = 5;

export type DemandStripArea =
  | { kind: "shared"; label: string }
  | { kind: "several" }
  | { kind: "none" };

export type DemandStripChip = {
  kind: "chip";
  request: ItemRequest;
  subcategoryLabel: string;
  prefsSummary: string;
};

export type DemandStripGroup = {
  kind: "group";
  subcategoryId: string;
  subcategoryLabel: string;
  count: number;
  /** Prefs that appear most often across the group (short summary). */
  commonPrefsSummary: string;
  requests: ItemRequest[];
  /** Best request for “I have this” when prefs are coherent enough. */
  primaryRequest: ItemRequest;
  /**
   * When false, prefs diverge — hide group “I have this” and expand-first (P1.1).
   */
  showGroupIHaveThis: boolean;
  /** Shared / several / omit — never fake “nearby” (P1.2). */
  area: DemandStripArea;
};

export type DemandStripEntry = DemandStripChip | DemandStripGroup;

export type GroupRecentRequestsOptions = {
  /** Resolve subcategory id → display name. */
  getSubcategoryLabel: (subcategoryId: string | undefined) => string;
  groupMin?: number;
};

function prefsKey(request: ItemRequest): string {
  return formatPreferenceSummary(request.preferenceAttrs ?? {});
}

function createdAtMs(request: ItemRequest): number {
  const t = request.createdAt?.getTime?.() ?? 0;
  return Number.isFinite(t) ? t : 0;
}

/** First segment of a location string (e.g. “Ikeja, Lagos” → “Ikeja”). */
export function areaLabelFromLocation(location: string | undefined): string {
  if (!location?.trim()) return "";
  return location.split(",")[0]?.trim() || location.trim();
}

/**
 * Derive group area: one shared label, “several”, or omit if unknown.
 */
export function summarizeDemandStripArea(
  requests: ItemRequest[],
): DemandStripArea {
  const unique = new Set<string>();
  for (const r of requests) {
    const label = areaLabelFromLocation(r.userLocation);
    if (label) unique.add(label);
  }
  if (unique.size === 0) return { kind: "none" };
  if (unique.size === 1) {
    return { kind: "shared", label: [...unique][0] };
  }
  return { kind: "several" };
}

/**
 * Prefs diverge when there is more than one distinct summary (including empty).
 * One shared summary (all empty or all the same ask) → coherent.
 */
export function prefsDivergeMaterially(requests: ItemRequest[]): boolean {
  const distinct = new Set(requests.map(prefsKey));
  return distinct.size > 1;
}

/**
 * Pick primary: mode prefs summary, then most recent within that mode.
 */
export function pickPrimaryRequest(requests: ItemRequest[]): ItemRequest {
  if (requests.length === 0) {
    throw new Error("pickPrimaryRequest requires at least one request");
  }
  const counts = new Map<string, number>();
  for (const r of requests) {
    const key = prefsKey(r);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  let modeKey = "";
  let modeCount = -1;
  for (const [key, count] of counts) {
    if (count > modeCount) {
      modeKey = key;
      modeCount = count;
    } else if (count === modeCount) {
      // Prefer non-empty prefs on ties
      if (key && !modeKey) modeKey = key;
    }
  }
  const inMode = requests.filter((r) => prefsKey(r) === modeKey);
  const pool = inMode.length > 0 ? inMode : requests;
  return [...pool].sort((a, b) => createdAtMs(b) - createdAtMs(a))[0];
}

/**
 * Pick a short “common asks” line: most frequent non-empty prefs summary in the group.
 */
export function summarizeCommonPrefs(requests: ItemRequest[]): string {
  const counts = new Map<string, number>();
  for (const r of requests) {
    const s = prefsKey(r);
    if (!s) continue;
    counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  if (counts.size === 0) return "";
  let best = "";
  let bestCount = 0;
  for (const [summary, count] of counts) {
    if (count > bestCount) {
      best = summary;
      bestCount = count;
    }
  }
  return best;
}

function newestInList(requests: ItemRequest[]): number {
  let max = 0;
  for (const r of requests) {
    max = Math.max(max, createdAtMs(r));
  }
  return max;
}

/**
 * Group recent approved requests for the homepage demand strip.
 * Requests without subcategory always become chips.
 * Groups sorted by count desc, then recency; chips after groups by recency.
 */
export function groupRecentRequestsForDemandStrip(
  requests: ItemRequest[],
  options: GroupRecentRequestsOptions,
): DemandStripEntry[] {
  const groupMin = options.groupMin ?? DEMAND_STRIP_GROUP_MIN;
  const bySub = new Map<string, ItemRequest[]>();
  const noSub: ItemRequest[] = [];

  for (const request of requests) {
    const sub = (request.subcategory ?? "").trim();
    if (!sub) {
      noSub.push(request);
      continue;
    }
    const list = bySub.get(sub) ?? [];
    list.push(request);
    bySub.set(sub, list);
  }

  const groups: DemandStripGroup[] = [];
  const chips: DemandStripChip[] = [];

  for (const [subcategoryId, list] of bySub) {
    const label = options.getSubcategoryLabel(subcategoryId) || subcategoryId;
    if (list.length >= groupMin) {
      const diverge = prefsDivergeMaterially(list);
      groups.push({
        kind: "group",
        subcategoryId,
        subcategoryLabel: label,
        count: list.length,
        commonPrefsSummary: summarizeCommonPrefs(list),
        requests: list,
        primaryRequest: pickPrimaryRequest(list),
        showGroupIHaveThis: !diverge,
        area: summarizeDemandStripArea(list),
      });
    } else {
      for (const request of list) {
        chips.push({
          kind: "chip",
          request,
          subcategoryLabel: label,
          prefsSummary: prefsKey(request),
        });
      }
    }
  }

  for (const request of noSub) {
    chips.push({
      kind: "chip",
      request,
      subcategoryLabel: request.title || "Request",
      prefsSummary: prefsKey(request),
    });
  }

  groups.sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return newestInList(b.requests) - newestInList(a.requests);
  });

  chips.sort((a, b) => createdAtMs(b.request) - createdAtMs(a.request));

  return [...groups, ...chips];
}
